import { Show, onCleanup, onMount } from "solid-js";

import { reqGetGameList } from "@/api/clientApi/front";
import { postSearchSportsData } from "@/api/clientApi/game";
import { SearchInputContainer } from "@/components/PC/Modals/GlobalSearch/SearchInput";
import { SearchPreviewItem } from "@/components/PC/Modals/GlobalSearch/SearchPreviewItem";
import { SearchSelect } from "@/components/PC/Modals/GlobalSearch/SearchSelect";
import { SearchSubCategory } from "@/components/PC/Modals/GlobalSearch/SearchSubCategory";
import { globalSearch, useGlobalSearch } from "@/hooks/useGlobalSearch";
import { usePreventVirtualKeyboardBodyScroll } from "@/hooks/usePreventVirtualKeyboardBodyScroll.ts";
import { useI18n } from "@/i18n";
import PageNav from "@/layouts/h5/components/PageNav";
import { overFlowFlagManager } from "@/lib/OverFlowFlag";
import { debounce } from "@/lib/generalUtils";
import { isMobile } from "@/lib/isMobile";
import { getLocalStorageItem, setLocalStorageItem } from "@/lib/localStorage";
import { useNavigate } from "@solidjs/router";

/* H5 响应式资料与 PC 共用 Signal, 避免重复定义 */
/* Fetch 逻辑先与 PC 分开, 不另统合成 hooks, 避免后续特殊需求改动逻辑过大不易维护 */

export default function H5GlobalSearchModalContainer() {
  // GameItem 的预览容器
  let scrollContainerRef: HTMLDivElement | undefined;
  const navigate = useNavigate();

  const { t } = useI18n();

  const {
    setSearchCasinoParams,
    setSearchSportsParams,
    setGameList,
    appendGameList,
    setGameListCount,
    setIsSearching,
    setIsLoadFinished,
    setApiLoading,
    setIsInit,
    setSearchHistory,
    setSearchValue,
    setCategoryIsOpen,
    setPopupVisible,
    resetGlobalSearchModal,
  } = useGlobalSearch();

  const gameItemPreviewScrollToTop = () => {
    if (scrollContainerRef) {
      scrollContainerRef.scrollTop = 0;
    }
  };

  // Casino Fetch
  const getCasinoGamesFetch = async (isLoadMore = false) => {
    const params = globalSearch().searchCasinoParams;
    if (
      !globalSearch().isInit &&
      params.name &&
      isLoadMore &&
      globalSearch().gameList.length === 0
    )
      return;

    if (!globalSearch().isInit) {
      setSearchCasinoParams({
        page: isLoadMore ? (params?.page || 1) + 1 : 1,
      });
    }

    setIsInit(false);
    setIsSearching(true);

    // 非加载更多模式时，重置完成状态
    if (!isLoadMore) {
      setIsLoadFinished(false);
    }

    try {
      const updatedParams = globalSearch().searchCasinoParams;
      const res = await reqGetGameList(updatedParams);

      /* prettier-ignore */
      const isQueryInHistory = getLocalStorageItem<string[]>("global-search",[]).includes(updatedParams.name);

      if (updatedParams.name && !isQueryInHistory) {
        updateSearchHistory(updatedParams.name);
      }

      const casinoList = Array.isArray(res.list) ? res.list : [];
      setApiLoading(false);

      if (isLoadMore) {
        appendGameList(casinoList);
      } else {
        setGameList(casinoList);
      }

      // 使用 total 字段准确判断是否还有更多数据
      const currentTotal =
        (isLoadMore ? globalSearch().gameList.length : 0) + casinoList.length;
      const hasMoreData = res.total && currentTotal < res.total;

      setGameListCount(res.total || currentTotal);
      setIsLoadFinished(!hasMoreData);
    } finally {
      setApiLoading(false);
      setIsSearching(false);
    }
  };

  // Sports Fetch
  const getSportsGamesFetch = async (isLoadMore = false) => {
    const params = globalSearch().searchSportsParams;
    if (params.query && isLoadMore && globalSearch().gameList.length === 0)
      return;

    setSearchSportsParams({
      page: isLoadMore ? (params?.page || 1) + 1 : 1,
    });

    setIsSearching(true);

    // 非加载更多模式时，重置完成状态
    if (!isLoadMore) {
      setIsLoadFinished(false);
    }

    try {
      const updatedParams = globalSearch().searchSportsParams;
      const res = await postSearchSportsData(updatedParams);
      if (!res) return;

      /* prettier-ignore */
      const isQueryInHistory = getLocalStorageItem<string[]>("global-search", []).includes(updatedParams.query)

      if (updatedParams.query && !isQueryInHistory) {
        updateSearchHistory(updatedParams.query);
      }

      const sportsList = Array.isArray(res.list) ? res.list : [];
      setApiLoading(false);

      if (isLoadMore) {
        appendGameList(sportsList);
      } else {
        setGameList(sportsList);
      }

      // 使用 data_count 字段准确判断是否还有更多数据
      const currentTotal =
        (isLoadMore ? globalSearch().gameList.length : 0) + sportsList.length;
      const hasMoreData = res.data_count && currentTotal < res.data_count;

      setGameListCount(res.data_count || currentTotal);
      setIsLoadFinished(!hasMoreData);
    } finally {
      setApiLoading(false);
      setIsSearching(false);
    }
  };

  // Fetch Mapping
  const fetchGames = async (isLoadMore = false) => {
    const categoryFetchMap: Record<
      string,
      (isLoadMore: boolean) => Promise<void>
    > = {
      Casino: getCasinoGamesFetch,
      Sports: getSportsGamesFetch,
    };

    const fetchFn =
      categoryFetchMap[globalSearch().curMainCategory] || getSportsGamesFetch;
    await fetchFn(isLoadMore);
  };

  const debouncedFetch = debounce(fetchGames, 300);

  // 初始化 Casino 资料
  onMount(() => {
    setIsInit(true);
    // 初始化时重置完成状态
    setIsLoadFinished(false);
    debouncedFetch();
  });

  //  ======= LocalStorage =======
  const updateSearchHistory = (value: string) => {
    const old = globalSearch().searchHistory;
    const newHistory = [value, ...old.filter((item: string) => item !== value)];
    setSearchHistory(newHistory);
    setLocalStorageItem("global-search", newHistory);
  };

  onMount(() => {
    const history = getLocalStorageItem<string[]>("global-search", []);
    setSearchHistory(history);
  });
  //  ======= LocalStorage =======

  onMount(() => {
    overFlowFlagManager.addFlag();
  });

  onCleanup(() => {
    overFlowFlagManager.removeFlag();
    setSearchValue("");
    setCategoryIsOpen(false);
    setPopupVisible(false);
  });

  usePreventVirtualKeyboardBodyScroll();

  return (
    <div id="global-search" class="bg-general-bg1 h-full w-full pb-[20px]">
      <PageNav
        title={t("search.search")}
        close={() => {
          resetGlobalSearchModal();
          navigate(-1);
        }}
      />

      <div class="bg-general-bg2 flex h-[100dvh] flex-col gap-[12px] p-[12px]">
        {/* 搜索框 */}
        <SearchInputContainer
          debouncedFetch={debouncedFetch}
          gameItemPreviewScrollToTop={gameItemPreviewScrollToTop}
        />

        {/* 子分类列表 - 为了 scroll 效果, 需要包裹一层 div */}
        <div
          class="overflow-y-auto overscroll-contain"
          ref={(el) => (scrollContainerRef = el)}
        >
          <div
            class="overflow-x-auto overflow-y-hidden"
            classList={{ "min-h-[36px]": isMobile() }}
          >
            <div class="flex w-max">
              <SearchSubCategory
                debouncedFetch={debouncedFetch}
                gameItemPreviewScrollToTop={gameItemPreviewScrollToTop}
              />
            </div>
          </div>

          {/* 筛选栏 - 只在 Casino 分类时显示 */}
          <Show when={globalSearch().gameList?.length > 0} fallback={<></>}>
            <div class="my-[12px]">
              <SearchSelect
                debouncedFetch={debouncedFetch}
                gameItemPreviewScrollToTop={gameItemPreviewScrollToTop}
              />
            </div>
          </Show>

          {/* 游戏列表 */}
          <SearchPreviewItem fetchGames={fetchGames} />
        </div>
      </div>
    </div>
  );
}
